import React, { useEffect, useRef, useState } from 'react';
import { useBodyScrollLock } from '../../hooks/useBodyScrollLock';
import { ArrowRight, Menu, X } from 'lucide-react';
import { useIdioma, type Traducir } from '../../i18n/idioma';

export interface NavLink {
    href: string;
    /** Clave del diccionario, no el texto: se traduce al pintar. */
    label: Parameters<Traducir>[0];
}

export const NAV_LINKS: NavLink[] = [
    { href: '#producto', label: 'lp.nav.laApp' },
    { href: '#empezar', label: 'lp.nav.comoEmpieza' },
    { href: '#academy', label: 'lp.nav.academy' },
    { href: '#tienda', label: 'lp.nav.tienda' },
    { href: '#preguntas', label: 'lp.nav.preguntas' },
];

interface LandingNavProps {
    /** Hay sesión activa: el CTA lleva al dashboard en vez de abrir el login. */
    signedIn: boolean;
    onPrimary: () => void;
}

/**
 * Barra de navegación de la landing.
 *
 * ISLA FLOTANTE: una pieza de navy sólido con esquinas redondeadas, separada
 * del borde superior y del ancho de la ventana, no una franja pegada de canto
 * a canto. El vídeo del hero se ve por encima y por los lados, y la portada
 * respira en vez de quedar decapitada. Al asentarse, la pieza se acerca al
 * borde, encoge, gana sombra y el CTA pasa de contorno a amarillo.
 *
 * Las decisiones de composición, y por qué:
 *
 * - **La tipografía manda en la esquina.** El archivo del logotipo es un icono
 *   de app —cuadrado, degradado azul, con «RAZAPRO» escrito dentro— así que a
 *   32 px repetía ilegiblemente el nombre que ya está a su lado. Ahora el
 *   icono es la marca y el nombre lo pone Archivo en 800; los enlaces bajan a
 *   14 px y a peso medio. Antes marca y menú iban los dos a 15 px y la barra
 *   no tenía jerarquía.
 * - **Alineada a la izquierda, con un filete entre marca y secciones.** Los
 *   enlaces centrados exigían una rejilla de tres columnas para que el menú no
 *   bailara al cambiar el ancho del botón («Empieza gratis» / «Ir al
 *   dashboard»); pegados al logotipo ese problema desaparece, y el filete es
 *   lo que convierte tres grupos sueltos en una barra con estructura.
 * - **El cambio de estado NO usa GSAP.** GSAP se carga en diferido y esta
 *   barra tiene que reaccionar desde el primer píxel de scroll; un listener
 *   pasivo es instantáneo y no depende de que llegue nada por red.
 * - **Nada de cristal.** La isla es navy SÓLIDO. `backdrop-filter` es la
 *   muletilla más repetida de las landings generadas y, sobre un vídeo en
 *   reproducción, obliga a recomponer el desenfoque en cada fotograma.
 * - **El menú móvil es un diálogo de verdad**, con el foco atrapado dentro.
 *   Una capa a pantalla completa que deja tabular por detrás está rota aunque
 *   se vea bien.
 */
const LandingNav: React.FC<LandingNavProps> = ({ signedIn, onPrimary }) => {
    const { t, idioma, cambiarIdioma } = useIdioma();
    const [asentada, setAsentada] = useState(false);
    const [menuOpen, setMenuOpen] = useState(false);
    const [activo, setActivo] = useState<string | null>(null);
    /* La isla está pasando por encima de una superficie navy y se pierde
       contra ella. Ver `medir()` y `.rp-nav[data-sobre-navy]` en landing.css. */
    const [sobreNavy, setSobreNavy] = useState(false);

    const panelRef = useRef<HTMLDivElement>(null);
    const cerrarRef = useRef<HTMLButtonElement>(null);

    /* Asentado + sección actual, en una sola medición por fotograma.
     *
     * La sección activa es LA ÚLTIMA cuyo borde superior ya cruzó la línea de
     * la barra, no «la que se ve más». Importa porque entre dos secciones del
     * menú hay bandas de fotografía que no son destino: con un observador de
     * franja, cruzarlas apagaba el indicador y el filete parpadeaba. Así, el
     * último destino alcanzado se queda marcado hasta que lo releva el
     * siguiente.
     *
     * Sobre la portada ninguna lo ha cruzado y `activo` queda en null —
     * correcto: «inicio» no está en el menú y no debe marcarse nada.
     *
     * `requestAnimationFrame` agrupa las medidas: leer geometría en cada
     * evento de scroll fuerza un reflujo por evento. */
    useEffect(() => {
        const secciones = NAV_LINKS
            .map((link) => ({ href: link.href, el: document.querySelector<HTMLElement>(link.href) }))
            .filter((s): s is { href: string; el: HTMLElement } => s.el !== null);

        /* Todas las superficies navy de la página, marcadas en su componente.
           Se resuelven una vez: son una media docena y no cambian.

           `pegado` se calcula aquí y no en cada fotograma porque leer estilo
           computado dentro del bucle de scroll cuesta un reflujo por elemento
           y por cuadro. */
        const navyes = [...document.querySelectorAll<HTMLElement>('[data-fondo="navy"]')].map(
            (el) => ({ el, pegado: getComputedStyle(el).position === 'sticky' }),
        );

        let pendiente = false;

        const medir = () => {
            pendiente = false;
            setAsentada(window.scrollY > 24);

            /* ── La línea de corte ───────────────────────────────────────
               TIENE QUE SER MAYOR que el punto donde aterriza un salto de
               ancla, o el marcador se queda una sección atrás.

               Aquí estuvo el fallo: el corte era 96 px (72 de barra + 24) y el
               salto deja la sección a 104 px, porque ese es el
               `scroll-padding-top: 6.5rem` que declara landing.css y que Lenis
               respeta. Con 96, la sección recién alcanzada NUNCA cruzaba el
               corte: pulsabas «Tienda» y el filete se quedaba en «Academy»,
               pulsabas «Cómo empieza» y se quedaba en «La app».

               Si alguien toca `scroll-padding-top`, tiene que mover esto con
               él. El margen de 8 px absorbe el redondeo a subpíxel del
               desplazamiento suave. */
            const corte = 104 + 8;
            let actual: string | null = null;
            for (const { href, el } of secciones) {
                if (el.getBoundingClientRect().top <= corte) actual = href;
            }
            setActivo(actual);

            /* ── ¿La isla está sobre navy? ────────────────────────────────
               La isla es navy. Cuando pasa por encima de una superficie navy
               —la portada, «Hoy tu negocio vive en diez lugares», las bandas
               de fotografía, el bloque de «De Chalán a Empresario», el
               cierre— desaparece contra el fondo: quedan el filete y la
               sombra, y poco más.

               No sirve mirar en qué SECCIÓN estás: el caso más visible es un
               bloque navy DENTRO de una sección clara (el de la Academy). Por
               eso se marcan las superficies una por una con `data-fondo="navy"`
               y aquí se pregunta si alguna cruza la franja de la barra.

               Basta con comprobarlo en vertical: todas esas superficies ocupan
               el ancho del contenedor o la ventana entera, así que si sus
               bordes superior e inferior abrazan la barra, está encima. */
            const franja = { arriba: 12, abajo: 12 + 74 };
            setSobreNavy(
                navyes.some(({ el, pegado }) => {
                    const r = el.getBoundingClientRect();
                    if (r.top > franja.abajo || r.bottom < franja.arriba) return false;
                    /* La portada va PEGADA (`sticky`): no se va de la pantalla,
                       se queda debajo mientras el resto de la página la tapa.
                       Su rectángulo dice «estoy aquí» para siempre, así que sin
                       esto la barra se quedaba blanca la página entera. Lo que
                       vale es cuánto se ha bajado — la misma regla con la que
                       el vídeo se detiene al quedar cubierto. */
                    if (pegado) return window.scrollY < el.offsetHeight * 0.92;
                    return true;
                }),
            );
        };

        const alMoverse = () => {
            if (pendiente) return;
            pendiente = true;
            requestAnimationFrame(medir);
        };

        medir();

        /* Y volver a medir cuando el diseño se asiente.
         *
         * Aquí estaba el fallo que se veía como «la barra no aparece hasta que
         * hago scroll»: esta primera medición corre justo al montar, cuando la
         * portada todavía no tiene alto —su foto no ha cargado—, así que
         * `offsetHeight` es 0 y la comprobación `scrollY < alto * 0.92` da
         * falso. La isla se quedaba NAVY sobre una foto oscura: estaba ahí,
         * pero no se distinguía. Al primer scroll se medía de nuevo, ya con
         * alto real, y se ponía blanca — de ahí que «apareciera».
         *
         * `load` cubre las imágenes; el observador cubre todo lo demás que
         * cambie de alto después (fuentes, vídeo, contenido que llega tarde).
         * Los dos llaman al mismo `alMoverse`, que ya viene limitado por
         * `requestAnimationFrame`, así que no cuesta cuadros de más. */
        window.addEventListener('load', alMoverse);
        const observador = new ResizeObserver(alMoverse);
        navyes.forEach(({ el }) => observador.observe(el));

        window.addEventListener('scroll', alMoverse, { passive: true });
        window.addEventListener('resize', alMoverse, { passive: true });
        return () => {
            window.removeEventListener('load', alMoverse);
            observador.disconnect();
            window.removeEventListener('scroll', alMoverse);
            window.removeEventListener('resize', alMoverse);
        };
    }, []);

    /* El menú móvil es una capa a pantalla completa: si el fondo sigue
     * desplazándose bajo el dedo, el panel se siente roto. Y el foco tiene que
     * entrar al abrirlo, quedarse dentro mientras está abierto, y volver al
     * botón que lo abrió al cerrarlo. */
    useEffect(() => {
        if (!menuOpen) return;

        const panel = panelRef.current;
        const previo = document.activeElement as HTMLElement | null;

        cerrarRef.current?.focus();

        const onKey = (e: KeyboardEvent) => {
            if (e.key === 'Escape') {
                setMenuOpen(false);
                return;
            }
            if (e.key !== 'Tab' || !panel) return;

            const focosables = panel.querySelectorAll<HTMLElement>('a[href], button');
            if (focosables.length === 0) return;

            const primero = focosables[0];
            const ultimo = focosables[focosables.length - 1];

            if (e.shiftKey && document.activeElement === primero) {
                e.preventDefault();
                ultimo.focus();
            } else if (!e.shiftKey && document.activeElement === ultimo) {
                e.preventDefault();
                primero.focus();
            }
        };

        window.addEventListener('keydown', onKey);
        return () => {
            window.removeEventListener('keydown', onKey);
            previo?.focus();
        };
    }, [menuOpen]);

    // Cuenta compartida: ver hooks/useBodyScrollLock.
    useBodyScrollLock(menuOpen);

    const cta = signedIn ? t('lp.cta.dashboard') : t('lp.cta.gratis');

    /* Selector de idioma.
     *
     * Va en la barra y no escondido en un menú: la app tenía uno desde hace
     * tiempo, pero enterrado en el menú del perfil, y el usuario que lo pidió
     * ni sabía que existía. Si no se ve, no está.
     *
     * Dos botones y no un desplegable: con solo dos opciones, un desplegable
     * cuesta dos clics para hacer lo mismo que uno, y además esconde cuál está
     * puesto. Aquí se ve de un vistazo cuál manda.
     *
     * `role="radiogroup"` y no una lista de botones sueltos: para un lector de
     * pantalla esto es UNA decisión con dos valores, no dos acciones
     * independientes. `aria-checked` dice cuál rige.
     *
     * `lang` en cada botón: sin él, un lector con la voz en español leería
     * "English" con fonética española. */
    const Selector: React.FC = () => (
        <div
            role="radiogroup"
            aria-label={t('lp.idioma.etiqueta')}
            className="rp-nav__idioma flex items-center gap-0.5 rounded-lg p-0.5"
        >
            {(['es', 'en'] as const).map((codigo) => (
                <button
                    key={codigo}
                    type="button"
                    role="radio"
                    aria-checked={idioma === codigo}
                    lang={codigo}
                    onClick={() => cambiarIdioma(codigo)}
                    className="rp-nav__idioma-btn rounded-[6px] px-2.5 py-1 text-[12px] font-semibold uppercase tracking-wide focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-400"
                >
                    {codigo}
                </button>
            ))}
        </div>
    );


    return (
        <>
            <a className="rp-skip" href="#contenido">
                {t('lp.nav.saltar')}
            </a>

            <header
                data-asentada={asentada ? '' : undefined}
                /* Con el menú abierto NO: el panel es navy a pantalla completa y
                   por debajo queda la isla, que en blanco se vería como un
                   recorte pegado encima. */
                data-sobre-navy={sobreNavy && !menuOpen ? '' : undefined}
                className="rp-nav fixed inset-x-0 top-0 z-40 px-4 sm:px-6 lg:px-8"
            >
                {/* `w-fit` en escritorio: la isla mide LO QUE MIDE SU CONTENIDO, no un
                    ancho fijo. Con 1240 px fijos volvía a aparecer el hueco muerto
                    entre el último enlace y el botón, que es justo lo que esta
                    forma venía a resolver. En el teléfono sí ocupa el ancho: ahí
                    dentro solo hay logotipo y botón de menú, y una pieza estrecha
                    centrada se vería como un error. */}
                <div className="rp-nav__isla mx-auto w-full max-w-[1240px] lg:w-fit">
                    <nav
                        aria-label={t('lp.nav.principal')}
                        /* Alineación a la IZQUIERDA, no enlaces centrados.
                           Centrados hacía falta una rejilla de tres columnas para
                           que el menú no bailara cuando el botón cambia de ancho
                           («Empieza gratis» / «Ir al dashboard»). Pegados al
                           logotipo, ese problema no existe: el botón crece hacia su
                           lado y no arrastra nada. Y la lectura queda como la de un
                           letrero —marca, luego secciones— en vez de como la de una
                           barra de plantilla con el menú flotando en medio. */
                        className="rp-nav__fila flex items-center px-5 sm:px-6"
                    >
                        <a
                            href="#inicio"
                            className="rp-nav__marca flex items-center gap-3 rounded-lg"
                            aria-label={t('lp.nav.marca')}
                        >
                            {/* El archivo es un icono de app: cuadrado con degradado
                                azul y con «RAZAPRO» ya escrito dentro. A 32 px ese
                                texto mide 4 px y solo ensucia, así que aquí el icono
                                actúa de MARCA y el nombre lo pone la tipografía. El
                                radio se iguala al del resto de la interfaz para que
                                no parezca una pegatina sobre la barra. */}
                            <img
                                src="/logo.webp"
                                alt=""
                                className="rp-nav__logo w-auto rounded-[9px] object-contain"
                            />
                            <span className="rp-nav__wordmark whitespace-nowrap text-white">
                                Raza Pro
                            </span>
                        </a>

                        {/* Filete vertical entre la marca y las secciones. Es lo que
                            convierte tres grupos sueltos en una barra con estructura,
                            y repite el lenguaje de filetes del resto de la página. */}
                        <span
                            aria-hidden="true"
                            className="rp-nav__filete mx-6 hidden h-7 w-px lg:block"
                        />

                        <ul className="hidden items-center gap-7 lg:flex">
                            {NAV_LINKS.map((link) => (
                                <li key={link.href}>
                                    <a
                                        href={link.href}
                                        /* `aria-current` marca el estado para quien
                                           oye la página y, de paso, es el gancho del
                                           filete amarillo en CSS: un solo atributo
                                           para las dos cosas, imposible que se
                                           desincronicen. */
                                        aria-current={activo === link.href ? 'true' : undefined}
                                        className="rp-nav__link rounded-lg"
                                    >
                                        {t(link.label)}
                                    </a>
                                </li>
                            ))}
                        </ul>

                        {/* Separación explícita del bloque de enlaces. Con la isla ajustada
                                a su contenido ya no hay espacio sobrante que `ml-auto`
                                pueda repartir, así que el hueco hay que declararlo. */}
                            <div className="ml-auto flex items-center gap-2 lg:ml-12">
                            {/* El envoltorio carga el `hidden`, no el botón.
                                `.rp-btn` fija `display:inline-flex` desde landing.css,
                                que Vite inyecta DESPUÉS de las utilidades de Tailwind:
                                a igual especificidad ganaba la última y el botón
                                reaparecía. Antes esto se resolvía con dos `!important`
                                encima del botón; un `<span>` lo arregla sin pelearse
                                con la cascada. */}
                            <span className="hidden lg:block">

                                <Selector />

                            </span>

                            <span className="hidden sm:block">
                                <button
                                    type="button"
                                    onClick={onPrimary}
                                    /* Sobre el hero va en contorno y solo se vuelve
                                       amarillo al asentarse. Si no, el amarillo sólido
                                       de la barra compite con el CTA del hero y la
                                       pantalla abre con dos primarios peleándose la
                                       mirada. */
                                    className={`rp-btn !px-5 !py-2.5 !text-[14px] ${
                                        asentada ? 'rp-btn--primary' : 'rp-btn--outline'
                                    }`}
                                >
                                    {cta}
                                    <ArrowRight className="h-4 w-4" aria-hidden="true" />
                                </button>
                            </span>
                            <button
                                type="button"
                                onClick={() => setMenuOpen(true)}
                                aria-label={t('lp.nav.abrirMenu')}
                                aria-expanded={menuOpen}
                                className="rp-nav__menu -mr-2 flex h-11 w-11 items-center justify-center rounded-lg text-white lg:hidden"
                            >
                                <Menu className="h-6 w-6" aria-hidden="true" />
                            </button>
                        </div>
                    </nav>
                </div>
            </header>

            {menuOpen && (
                <div
                    ref={panelRef}
                    role="dialog"
                    aria-modal="true"
                    aria-label={t('lp.nav.menuPrincipal')}
                    /* Columna flexible: la lista crece, y el botón y el pie
                       quedan anclados abajo. Antes el panel dejaba media
                       pantalla de navy vacío bajo el CTA. */
                    className="fixed inset-0 z-50 flex flex-col bg-navy-850 lg:hidden"
                >
                    <div className="flex h-[72px] items-center justify-between px-5 sm:px-8">
                        <span className="text-[15px] font-bold tracking-tight text-white">
                            Raza Pro
                        </span>
                        <div className="flex items-center gap-3">

                            <Selector />

                            <button
                                ref={cerrarRef}
                            type="button"
                            onClick={() => setMenuOpen(false)}
                            aria-label={t('lp.nav.cerrarMenu')}
                            className="-mr-2 flex h-11 w-11 items-center justify-center rounded-lg text-white"
                        >
                            <X className="h-6 w-6" aria-hidden="true" />
                        </button>
                        </div>
                    </div>
                    <ul className="flex-1 min-h-0 overflow-y-auto px-5 sm:px-8">
                        {NAV_LINKS.map((link) => (
                            <li key={link.href} className="border-b border-white/10">
                                <a
                                    href={link.href}
                                    aria-current={activo === link.href ? 'true' : undefined}
                                    onClick={() => setMenuOpen(false)}
                                    className="block rounded-lg py-5 text-[26px] font-bold tracking-tight text-white"
                                >
                                    {t(link.label)}
                                </a>
                            </li>
                        ))}
                    </ul>

                    <div className="px-5 pb-8 pt-6 sm:px-8">
                        <button
                            type="button"
                            onClick={() => {
                                setMenuOpen(false);
                                onPrimary();
                            }}
                            className="rp-btn rp-btn--primary w-full"
                        >
                            {cta}
                            <ArrowRight className="h-4 w-4" aria-hidden="true" />
                        </button>

                        {/* Pie del panel. Rellena el vacío que quedaba bajo el
                            botón con las dos cosas que alguien busca en un menú
                            de teléfono: de quién es esto, y cómo se les habla. */}
                        <div className="mt-7 flex flex-wrap items-center justify-between gap-x-6 gap-y-3 border-t border-white/10 pt-6 text-[14px]">
                            <a
                                href="https://www.razaperronaacademy.com"
                                target="_blank"
                                rel="noopener noreferrer"
                                className="rounded-lg text-acero-400 transition-colors hover:text-white"
                            >
                            {t('lp.nav.deAcademy')}
                            </a>
                            <a
                                href="https://wa.me/14806700484"
                                target="_blank"
                                rel="noopener noreferrer"
                                className="rounded-lg font-medium text-white"
                            >
                                WhatsApp +1 (480) 670-0484
                            </a>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

export default LandingNav;
